import { useEffect } from "react"
import { AppRoute, isBlogPostRoute, isBlogRoute, isBoardRoute, isEphemeralSessionRoute, isSessionRoute, isTemplateDetailRoute } from "./RouteProvider"
import { logPageView } from "../../models/analytics"
import { TEMPLATES } from "../../models/template"
import { hasRealContent } from "./templateSeoConfig"
import { getBlogPost } from "../../components/blog/posts"

const ROBOTS_META_NAME = "robots"
const NOINDEX = "noindex,nofollow"
const INDEX = "index,follow"

const DEFAULT_TITLE =
  "Retroboard — Real-time collaborative retrospective boards for any team"
const DEFAULT_DESCRIPTION =
  "Run better retrospectives with Retroboard. Real-time collaborative boards for any team — build your own or start from Sailboat, 4Ls, Start/Stop/Continue, Mad/Sad/Glad and more. Unlimited and free."
const BLOG_TITLE = "Blog — Retroboard"
const BLOG_DESCRIPTION = "Notes on building and using Retroboard."
const CANONICAL_ORIGIN = "https://retroboard.net"

const JSONLD_TEMPLATE_ID = "route-head-template-jsonld"
const JSONLD_BLOGPOST_ID = "route-head-blogpost-jsonld"

function upsertMeta(name: string, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!meta) {
    meta = document.createElement("meta")
    meta.setAttribute("name", name)
    document.head.appendChild(meta)
  }
  meta.setAttribute("content", content)
}

function upsertCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement("link")
    link.setAttribute("rel", "canonical")
    document.head.appendChild(link)
  }
  link.setAttribute("href", href)
}

function upsertTemplateJsonLd(templateId: string, title: string, description: string, steps: string[]) {
  const payload = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `How to run a ${title} retrospective`,
    description,
    step: steps.map((step, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: step,
    })),
  }
  let script = document.getElementById(JSONLD_TEMPLATE_ID) as HTMLScriptElement | null
  if (!script) {
    script = document.createElement("script")
    script.type = "application/ld+json"
    script.id = JSONLD_TEMPLATE_ID
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(payload)
  script.dataset.templateId = templateId
}

function removeTemplateJsonLd() {
  const script = document.getElementById(JSONLD_TEMPLATE_ID)
  if (script) script.remove()
}

function upsertBlogPostJsonLd(slug: string, title: string, description: string, isoDate: string, canonical: string, image?: string, author?: string, authorUrl?: string) {
  const payload = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    datePublished: isoDate,
    dateModified: isoDate,
    mainEntityOfPage: canonical,
    ...(image ? { image: [image] } : {}),
    author: author
      ? { "@type": "Person", name: author, ...(authorUrl ? { url: authorUrl } : {}) }
      : { "@type": "Organization", name: "Retroboard" },
    publisher: {
      "@type": "Organization",
      name: "Retroboard",
      logo: { "@type": "ImageObject", url: `${CANONICAL_ORIGIN}/pwa-512x512.png` },
    },
  }
  let script = document.getElementById(JSONLD_BLOGPOST_ID) as HTMLScriptElement | null
  if (!script) {
    script = document.createElement("script")
    script.type = "application/ld+json"
    script.id = JSONLD_BLOGPOST_ID
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(payload)
  script.dataset.slug = slug
}

function removeBlogPostJsonLd() {
  const script = document.getElementById(JSONLD_BLOGPOST_ID)
  if (script) script.remove()
}

export function RouteHead({ route }: { route: AppRoute }): null {
  useEffect(() => {
    // 1. Robots meta — noindex on private routes AND on placeholder template pages.
    const isPrivateAppRoute = isBoardRoute(route) || isSessionRoute(route)
    const isPlaceholderTemplate =
      isTemplateDetailRoute(route) && !hasRealContent(route.templateId)
    const shouldNoindex = isPrivateAppRoute || isPlaceholderTemplate
    upsertMeta(ROBOTS_META_NAME, shouldNoindex ? NOINDEX : INDEX)

    // 2. Template-detail routes get per-template title, description, canonical,
    //    and HowTo JSON-LD.
    if (isTemplateDetailRoute(route)) {
      const template = TEMPLATES.find((t) => t.id === route.templateId)
      if (template) {
        const title = `${template.title} retrospective template — Retroboard`
        const description = template.description
        document.title = title
        upsertMeta("description", description)
        upsertCanonical(`${CANONICAL_ORIGIN}/templates/${template.id}`)
        upsertTemplateJsonLd(
          template.id,
          template.title,
          description,
          template.columns.map((c) => `${c.title}: ${c.description ?? ""}`.trim()),
        )
        removeBlogPostJsonLd()
        return
      }
    }

    // 3. Blog post routes get per-post title, description, canonical, and
    //    BlogPosting JSON-LD.
    if (isBlogPostRoute(route)) {
      const post = getBlogPost(route.slug)
      if (post) {
        const title = `${post.title} — Retroboard`
        const canonical = `${CANONICAL_ORIGIN}/blog/${post.slug}`
        document.title = title
        upsertMeta("description", post.description)
        upsertCanonical(canonical)
        upsertBlogPostJsonLd(
          post.slug,
          post.title,
          post.description,
          post.date,
          canonical,
          post.image ? `${CANONICAL_ORIGIN}${post.image}` : undefined,
          post.author,
          post.authorUrl,
        )
        removeTemplateJsonLd()
        return
      }
      // Unknown slug — the user is on a /blog/... URL that doesn't match a
      // real post (stale link, typo). Fall through to blog-list meta rather
      // than the site default so the SEO signal matches the surface the SPA
      // is actually rendering ("Post not found. Back to blog.").
    }

    // 4. Blog list route (and unknown blog post slug — see above).
    if (isBlogRoute(route) || isBlogPostRoute(route)) {
      document.title = BLOG_TITLE
      upsertMeta("description", BLOG_DESCRIPTION)
      upsertCanonical(`${CANONICAL_ORIGIN}/blog`)
      removeTemplateJsonLd()
      removeBlogPostJsonLd()
      return
    }

    // 5. Default: restore site meta and clear any per-route JSON-LD.
    document.title = DEFAULT_TITLE
    upsertMeta("description", DEFAULT_DESCRIPTION)
    upsertCanonical(`${CANONICAL_ORIGIN}/`)
    removeTemplateJsonLd()
    removeBlogPostJsonLd()
  }, [route])

  // Emit a GA page_view on every route change. Firebase Analytics only
  // auto-fires ONE page_view (on SDK init), so internal SPA navigation is
  // otherwise invisible to Analytics. This effect runs after the DOM-updating
  // one above, so document.title is fresh.
  //
  // For private routes (board/session), redact the ids in page_location — the
  // URL functions like a password for anyone with the link, and we don't want
  // to hand those to a third-party analytics endpoint.
  useEffect(() => {
    const pageLocation = redactedPageLocation(route)
    logPageView(pageLocation, document.title)
  }, [route])

  return null
}

function redactedPageLocation(route: AppRoute): string {
  if (isBoardRoute(route)) return `${CANONICAL_ORIGIN}/board/[id]`
  if (isSessionRoute(route)) return `${CANONICAL_ORIGIN}/board/[id]/session/[id]`
  // Ephemeral state encodes user card text — don't ship it to a third party.
  if (isEphemeralSessionRoute(route)) return `${CANONICAL_ORIGIN}/try/${route.templateId}`
  return `${CANONICAL_ORIGIN}${route.pathBuilder()}`
}
